import { startTracking, type TacSdk } from '@tonappchain/sdk';
import type { ChooseStakingParams, ChooseImmediateWithdrawParams, DecCoin } from '@/types';
import { ethers } from 'ethers';
import { createPublicClient, http, type Address, type Chain } from 'viem';
import { DISTRIBUTION_ABI, STAKINGI_ABI, TAC_VESTING_ABI } from './ABI';
import { displayTimeByUnix, formatTokenAmount } from '@/utils/common';
import { ENVS } from '@/constants/config';

export class TacService {
  private tacSdk: TacSdk;
  private viemClient;
  private contractAddress: string;
  private distributionAddress: string;

  constructor({
    tacSdk,
    chain,
    contractAddress,
    distributionAddress
  }: {
    tacSdk: TacSdk;
    chain: Chain;
    contractAddress: string;
    distributionAddress: string;
  }) {
    this.tacSdk = tacSdk;
    this.contractAddress = contractAddress;
    this.distributionAddress = distributionAddress;
    this.viemClient = createPublicClient({
      chain: chain,
      transport: http(chain.rpcUrls.default.http[0])
    });
  }

  async chooseStaking(params: ChooseStakingParams, sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'chooseStaking(bytes,bytes)',
      encodedParameters: this.encodeStakingParams(params)
    };

    console.log('send staking tx:', evmProxyMsg);
    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender, []);
    console.log('staking result:', result);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error((result as any).sendTransactionResult.error || 'Transaction failed to send');
    }

    return result;
  }

  async withdraw(amount: bigint, sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'withdraw(bytes,bytes)',
      encodedParameters: this.encodeWithdrawParams(amount)
    };

    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error((result as any).sendTransactionResult.error || 'Withdraw transaction failed to send');
    }

    return result;
  }

  async undelegate(amount: bigint, sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'undelegate(bytes,bytes)',
      encodedParameters: this.encodeUndelegateParams(amount)
    };

    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error((result as any).sendTransactionResult.error || 'Undelegate transaction failed to send');
    }

    return result;
  }

  async claimDelegatorRewards(sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'claimDelegatorRewards(bytes,bytes)',
      encodedParameters: '0x'
    };

    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error((result as any).sendTransactionResult.error || 'Claim rewards transaction failed to send');
    }

    return result;
  }

  async withdrawFromAccount(sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'withdrawFromAccount(bytes,bytes)',
      encodedParameters: '0x'
    };

    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error(
        (result as any).sendTransactionResult.error || 'Withdraw from account transaction failed to send'
      );
    }

    return result;
  }

  // 查询用户状态
  async getUserInfo(userTonAddress: string): Promise<any> {
    try {
      // 使用 viem 查询合约状态
      const result = await this.viemClient.readContract({
        address: this.contractAddress as Address,
        abi: TAC_VESTING_ABI,
        functionName: 'info',
        args: [userTonAddress]
      });
      const [choiceStartTime, stakingAccount, validatorAddress, userTotalRewards, unlocked, withdrawn] = result;

      return {
        choiceStartTime: Number(choiceStartTime),
        stakingAccount: stakingAccount,
        validatorAddress: validatorAddress,
        userTotalRewards: userTotalRewards.toString(),
        unlocked: unlocked.toString(),
        withdrawn: withdrawn.toString()
      };
    } catch (error) {
      console.error('Failed to get user info:', error);
      throw error;
    }
  }

  // 检查用户是否已经做出选择
  async hasUserMadeChoice(userTonAddress: string): Promise<boolean> {
    try {
      const userInfo = await this.getUserInfo(userTonAddress);
      // choiceStartTime != 0 表示用户已经做出选择
      return userInfo.choiceStartTime !== 0;
    } catch (error) {
      console.error('Failed to check user choice:', error);
      return false;
    }
  }

  // 检查用户选择的模式
  async getUserChoiceMode(userTonAddress: string): Promise<'staking' | 'immediate' | 'none'> {
    try {
      const userInfo = await this.getUserInfo(userTonAddress);

      if (userInfo.choiceStartTime === 0) {
        return 'none'; // 还没有做选择
      }

      // 检查 stakingAccount 是否为零地址
      const zeroAddress = '0x0000000000000000000000000000000000000000';
      if (userInfo.stakingAccount.toLowerCase() !== zeroAddress.toLowerCase()) {
        return 'staking'; // 选择了质押
      } else {
        return 'immediate'; // 选择了即时提取
      }
    } catch (error) {
      console.error('Failed to get user choice mode:', error);
      return 'none';
    }
  }

  // 获取用户可提取的金额（优先使用合约方法）
  async getUserAvailableAmount(userTonAddress: string): Promise<bigint> {
    try {
      // 首先尝试使用合约的 getAvailable 方法
      const availableFromContract = await this.getAvailable(userTonAddress);
      if (availableFromContract > 0) {
        return availableFromContract;
      }

      // 如果合约方法失败，回退到计算方式
      const userInfo = await this.getUserInfo(userTonAddress);
      return BigInt(userInfo.unlocked) - BigInt(userInfo.withdrawn);
    } catch (error) {
      console.error('Failed to get user available amount:', error);
      return BigInt(0);
    }
  }

  // 获取用户已解锁金额（优先使用合约方法）
  async getUserUnlockedAmount(userTonAddress: string): Promise<bigint> {
    try {
      // 首先尝试使用合约的 getUnlocked 方法
      const unlockedFromContract = await this.getUnlocked(userTonAddress);
      if (unlockedFromContract > 0) {
        return unlockedFromContract;
      }

      // 如果合约方法失败，回退到从 userInfo 获取
      const userInfo = await this.getUserInfo(userTonAddress);
      return BigInt(userInfo.unlocked);
    } catch (error) {
      console.error('Failed to get user unlocked amount:', error);
      return BigInt(0);
    }
  }

  // 获取合约基本信息
  async getContractInfo() {
    try {
      // 尝试使用 multicall，如果失败则回退到单独调用
      try {
        const results = await this.viemClient.multicall({
          contracts: [
            {
              address: this.contractAddress as Address,
              abi: TAC_VESTING_ABI,
              functionName: 'stepDuration'
            },
            {
              address: this.contractAddress as Address,
              abi: TAC_VESTING_ABI,
              functionName: 'merkleRoot'
            },
            {
              address: this.contractAddress as Address,
              abi: TAC_VESTING_ABI,
              functionName: 'VESTING_STEPS'
            }
          ]
        });

        // 检查所有调用是否成功
        if (results.some((result) => result.status === 'failure')) {
          throw new Error('Some contract calls failed');
        }

        const [stepDurationResult, merkleRootResult, vestingStepsResult] = results;

        return {
          stepDuration: stepDurationResult.result?.toString() || '0',
          merkleRoot: merkleRootResult.result || '0x',
          vestingSteps: vestingStepsResult.result?.toString() || '0'
        };
      } catch (multicallError) {
        console.warn('Multicall failed, falling back to individual calls:', multicallError);

        // 回退到原来的方式
        const [stepDuration, merkleRoot, vestingSteps] = await Promise.all([
          this.viemClient.readContract({
            address: this.contractAddress as Address,
            abi: TAC_VESTING_ABI,
            functionName: 'stepDuration'
          }),
          this.viemClient.readContract({
            address: this.contractAddress as Address,
            abi: TAC_VESTING_ABI,
            functionName: 'merkleRoot'
          }),
          this.viemClient.readContract({
            address: this.contractAddress as Address,
            abi: TAC_VESTING_ABI,
            functionName: 'VESTING_STEPS'
          })
        ]);

        return {
          stepDuration: stepDuration.toString(),
          merkleRoot: merkleRoot,
          vestingSteps: vestingSteps.toString()
        };
      }
    } catch (error) {
      console.error('Failed to get contract info:', error);
      throw error;
    }
  }

  // 获取释放参数
  async getVestingParams() {
    try {
      // 尝试使用 multicall，如果失败则回退到单独调用
      try {
        const results = await this.viemClient.multicall({
          contracts: [
            {
              address: this.contractAddress as Address,
              abi: TAC_VESTING_ABI,
              functionName: 'stepDuration'
            },
            {
              address: this.contractAddress as Address,
              abi: TAC_VESTING_ABI,
              functionName: 'VESTING_STEPS'
            }
          ]
        });

        // 检查所有调用是否成功
        if (results.some((result) => result.status === 'failure')) {
          throw new Error('Some contract calls failed');
        }

        const [stepDurationResult, vestingStepsResult] = results;

        return {
          stepDuration: Number(stepDurationResult.result || 0), // Convert to number for calculations
          vestingSteps: Number(vestingStepsResult.result || 0)
        };
      } catch (multicallError) {
        console.warn('Multicall failed, falling back to individual calls:', multicallError);

        // 回退到原来的方式
        const [stepDuration, vestingSteps] = await Promise.all([
          this.viemClient.readContract({
            address: this.contractAddress as Address,
            abi: TAC_VESTING_ABI,
            functionName: 'stepDuration'
          }),
          this.viemClient.readContract({
            address: this.contractAddress as Address,
            abi: TAC_VESTING_ABI,
            functionName: 'VESTING_STEPS'
          })
        ]);

        return {
          stepDuration: Number(stepDuration), // Convert to number for calculations
          vestingSteps: Number(vestingSteps)
        };
      }
    } catch (error) {
      console.error('Failed to get vesting params:', error);
      throw error;
    }
  }

  // 计算下一次解锁时间
  async calculateNextUnlockTime(userTonAddress: string): Promise<{
    nextUnlockTime: Date | null;
    timeUntilUnlock: number;
    currentStep: number;
    totalSteps: number;
    isFullyVested: boolean;
  }> {
    try {
      const [userInfo, vestingParams] = await Promise.all([this.getUserInfo(userTonAddress), this.getVestingParams()]);

      const choiceStartTime = userInfo.choiceStartTime;
      console.log(vestingParams, 'params');
      const { stepDuration, vestingSteps } = vestingParams;

      // If user hasn't made a choice yet
      if (choiceStartTime === 0) {
        return {
          nextUnlockTime: null,
          timeUntilUnlock: 0,
          currentStep: 0,
          totalSteps: vestingSteps,
          isFullyVested: false
        };
      }

      const now = Math.floor(Date.now() / 1000); // Current timestamp in seconds
      const startTime = choiceStartTime;
      const timeElapsed = now - startTime;

      // Calculate current step (0-based)
      const currentStep = Math.floor(timeElapsed / stepDuration);

      // Check if fully vested
      const isFullyVested = currentStep >= vestingSteps;

      if (isFullyVested) {
        return {
          nextUnlockTime: null,
          timeUntilUnlock: 0,
          currentStep: vestingSteps,
          totalSteps: vestingSteps,
          isFullyVested: true
        };
      }

      // Calculate next unlock time
      const nextStep = currentStep + 1;
      const nextUnlockTimestamp = startTime + nextStep * stepDuration;
      const nextUnlockTime = new Date(nextUnlockTimestamp * 1000);
      const timeUntilUnlock = nextUnlockTimestamp - now;

      return {
        nextUnlockTime,
        timeUntilUnlock: Math.max(0, timeUntilUnlock),
        currentStep: Math.max(0, currentStep),
        totalSteps: vestingSteps,
        isFullyVested: false
      };
    } catch (error) {
      console.error('Failed to calculate next unlock time:', error);
      return {
        nextUnlockTime: null,
        timeUntilUnlock: 0,
        currentStep: 0,
        totalSteps: 0,
        isFullyVested: false
      };
    }
  }

  // 获取用户可用金额（直接从合约查询）
  async getAvailable(userTonAddress: string): Promise<bigint> {
    try {
      const result = await this.viemClient.readContract({
        address: this.contractAddress as Address,
        abi: TAC_VESTING_ABI,
        functionName: 'getAvailable',
        args: [userTonAddress]
      });
      return result as bigint;
    } catch (error) {
      console.error('Failed to get available amount:', error);
      return BigInt(0);
    }
  }

  // 获取用户已解锁金额（直接从合约查询）
  async getUnlocked(userTonAddress: string): Promise<bigint> {
    try {
      console.log('Querying unlocked amount for:', userTonAddress);

      const result = await this.viemClient.readContract({
        address: this.contractAddress as Address,
        abi: TAC_VESTING_ABI,
        functionName: 'getUnlocked',
        args: [userTonAddress]
      });

      console.log('Unlocked amount from contract:', result);
      return result as bigint;
    } catch (error) {
      console.error('Failed to get unlocked amount:', error);
      return BigInt(0);
    }
  }

  async getDelegationRewards({
    delegatorAddress,
    validatorAddress
  }: {
    delegatorAddress: `0x${string}`;
    validatorAddress: string; // Cosmos 风格 validator 地址，如 "validator1xyz..."
  }) {
    try {
      const rewards = (await this.viemClient.readContract({
        address: this.distributionAddress as Address,
        abi: DISTRIBUTION_ABI,
        functionName: 'delegationRewards',
        args: [delegatorAddress, validatorAddress]
      })) as DecCoin[];

      return formatTokenAmount(rewards?.[0].amount, rewards?.[0]?.precision);
    } catch (error) {
      console.error('Failed to get delegation rewards:', error);
      throw error;
    }
  }

  async getBalance(address: string): Promise<{ value: bigint; display: string }> {
    try {
      const result = await this.viemClient.getBalance({
        address: address as Address
      });
      console.log('balance:', result);
      return {
        value: result as bigint,
        display: formatTokenAmount(result as bigint, 18)
      };
    } catch (error) {
      console.error('Failed to get balance:', error);
      return {
        value: BigInt(0),
        display: '0'
      };
    }
  }

  async getUnbondingDelegation({
    delegatorAddress,
    validatorAddress
  }: {
    delegatorAddress: `0x${string}`;
    validatorAddress: string; // Cosmos 风格 validator 地址，如 "validator1xyz..."
  }): Promise<
    {
      isCompleted: boolean;
      balance: string;
      completionTime: number;
      completeTimeDisplay: string;
      isWithdrawn: boolean;
    }[]
  > {
    try {
      const res = (await this.viemClient.readContract({
        address: ENVS.TAC_STAKING_CONTRACT_ADDRESS as Address,
        abi: STAKINGI_ABI,
        functionName: 'unbondingDelegation',
        args: [delegatorAddress, validatorAddress]
      })) as any;

      console.log(res, 'bond');

      return res?.entries?.map((it: any) => {
        const now = Math.floor(Date.now() / 1000);
        const isCompleted = Number(it.completionTime) <= now;
        return {
          isCompleted,
          balance: formatTokenAmount(it.balance, 18),
          completionTime: Number(it.completionTime),
          completeTimeDisplay: displayTimeByUnix(Number(it.completionTime)),
          isWithdrawn: Number(it.balance) === 0
        };
      });
    } catch (error) {
      console.error('Failed to get delegation rewards:', error);
      throw error;
    }
  }

  // 等待交易确认的方法
  async waitForTransactionConfirmation(transactionLinker: any, isRoundTrip = true) {
    console.log('start tracking tx:', transactionLinker);
    const stages = await startTracking(transactionLinker, this.tacSdk.network, {
      // customLiteSequencerEndpoints: this.tacSdk.liteSequencerEndpoints,
      returnValue: true,
      tableView: false
      // delay: 5000, // Poll every 5 seconds
      // maxIterationCount: 10000 // Maximum 60 iterations (5 minutes)
    });

    console.log('stages:', stages, isRoundTrip);

    if (!stages) {
      throw new Error(`Transaction call tracking failed`);
    }

    // let lastStage;
    // if (isRoundTrip) {
    //   lastStage = StageName.EXECUTED_IN_TON;
    // } else {
    //   lastStage = StageName.EXECUTED_IN_TAC;
    // }

    // if (!stages[lastStage].exists) {
    //   console.log(JSON.stringify(stages, null, 2));
    //   throw new Error(`Transaction call not executed in ${lastStage}`);
    // }

    // console.log(`Transaction call successfully executed in ${lastStage}`);

    // const tacTxHash = stages[StageName.EXECUTED_IN_TAC].stageData!.transactions![0].hash;
    // const txReceipt = await this.viemClient.waitForTransactionReceipt({
    //   hash: tacTxHash as `0x${string}`
    // });

    // if (!txReceipt) {
    //   throw new Error(`Transaction receipt ${tacTxHash} call not found in TAC`);
    // }

    return stages;
  }

  private encodeStakingParams(params: ChooseStakingParams): string {
    // ChooseStakingParams struct:
    // string validatorAddress
    // uint256 userTotalRewards
    // bytes32[] merkleProof
    const abiCoder = new ethers.AbiCoder();

    // 验证参数
    console.log('Encoding staking params:', params);

    return abiCoder.encode(
      ['tuple(string,uint256,bytes32[])'],
      [[params.validatorAddress, params.userTotalRewards, params.merkleProof]]
    );
  }

  private encodeImmediateWithdrawParams(params: ChooseImmediateWithdrawParams): string {
    // ChooseImmediateWithdrawParams struct:
    // uint256 userTotalRewards
    // bytes32[] merkleProof

    const abiCoder = new ethers.AbiCoder();

    // 根据 Solidity 合约期望，struct 应该使用 tuple 编码
    const encoded = abiCoder.encode(['tuple(uint256,bytes32[])'], [[params.userTotalRewards, params.merkleProof]]);

    console.log('Final encoded result:', encoded);
    return encoded;
  }

  async chooseImmediateWithdraw(params: ChooseImmediateWithdrawParams, sender: any) {
    const evmProxyMsg = {
      evmTargetAddress: this.contractAddress,
      methodName: 'chooseImmediateWithdraw(bytes,bytes)',
      encodedParameters: this.encodeImmediateWithdrawParams(params)
    };

    console.log('发送即时提取交易:', evmProxyMsg);
    const result = await this.tacSdk.sendCrossChainTransaction(evmProxyMsg, sender, []);
    console.log('即时提取交易发送结果:', result);

    // 检查交易是否成功
    if (!(result as any).sendTransactionResult.success) {
      throw new Error((result as any).sendTransactionResult.error || 'Immediate withdraw transaction failed to send');
    }

    return result;
  }

  private encodeWithdrawParams(amount: bigint): string {
    // withdraw function expects: uint256 amount
    const abiCoder = new ethers.AbiCoder();
    return abiCoder.encode(['uint256'], [amount]);
  }

  private encodeUndelegateParams(amount: bigint): string {
    // undelegate function expects: uint256 amount
    const abiCoder = new ethers.AbiCoder();
    return abiCoder.encode(['uint256'], [amount]);
  }
}
